import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2, RotateCcw, Save, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = [["text", "Short text"], ["textarea", "Long text"], ["email", "Email"], ["tel", "Phone"], ["date", "Date"],
  ["select", "Dropdown"], ["radio", "Radio buttons"], ["checkbox", "Checkboxes"]];
const LOCKED = ["full_name", "phone", "email"];
const PROTECTED = [...LOCKED, "course_interest"];
const needsOptions = (t) => ["select", "radio", "checkbox"].includes(t);

function FieldRow({ f, i, n, onChange, onMove, onDelete }) {
  const locked = LOCKED.includes(f.key);
  return (
    <div className={`rounded-xl border p-4 ${f.enabled ? "border-border" : "border-dashed border-border bg-secondary/40 opacity-70"}`} data-testid={`form-field-row-${f.key}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex flex-col gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`field-up-${f.key}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === n - 1} onClick={() => onMove(i, 1)} data-testid={`field-down-${f.key}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="grid min-w-[240px] flex-1 gap-3 sm:grid-cols-[1fr_170px]">
          <div><Label className="text-xs">Question / label</Label><Input value={f.label} onChange={(e) => onChange({ label: e.target.value })} data-testid={`field-label-${f.key}`} /></div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={f.type} onValueChange={(v) => onChange({ type: v })} disabled={locked}>
              <SelectTrigger data-testid={`field-type-${f.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {needsOptions(f.type) ? (
            <div className="sm:col-span-2">
              <Label className="text-xs">Options (one per line)</Label>
              <Textarea rows={3} value={(f.options || []).join("\n")} onChange={(e) => onChange({ options: e.target.value.split("\n") })} data-testid={`field-options-${f.key}`} />
            </div>
          ) : (
            <div className="sm:col-span-2"><Label className="text-xs">Placeholder</Label><Input value={f.placeholder || ""} onChange={(e) => onChange({ placeholder: e.target.value })} /></div>
          )}
        </div>
        <div className="flex flex-col gap-2 text-sm">
          {locked ? <span className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> Always required</span> : (<>
            <label className="flex items-center gap-2"><Switch checked={f.enabled} onCheckedChange={(v) => onChange({ enabled: v })} data-testid={`field-enabled-${f.key}`} /> Enabled</label>
            <label className="flex items-center gap-2"><Switch checked={f.required} onCheckedChange={(v) => onChange({ required: v })} data-testid={`field-required-${f.key}`} /> Required</label>
          </>)}
          {!PROTECTED.includes(f.key) && (
            <Button size="sm" variant="ghost" className="justify-start text-rose-600" onClick={onDelete} data-testid={`field-delete-${f.key}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EnquiryFormTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/enquiry-form").then((r) => setForm(r.data)); }, []);
  if (!form) return <Skeleton className="h-64" />;

  const setField = (i, patch) => setForm((f) => ({ ...f, fields: f.fields.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const move = (i, d) => setForm((f) => { const a = [...f.fields]; [a[i], a[i + d]] = [a[i + d], a[i]]; return { ...f, fields: a }; });
  const remove = (i) => setForm((f) => ({ ...f, fields: f.fields.filter((_, j) => j !== i) }));
  const add = () => setForm((f) => ({ ...f, fields: [...f.fields, { key: `custom_${Date.now().toString(36)}`, label: "New question", type: "text", required: false, enabled: true, options: [], placeholder: "" }] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, fields: form.fields.map((x) => ({ ...x, options: (x.options || []).map((o) => o.trim()).filter(Boolean) })) };
      const { data } = await api.put("/admin/enquiry-form", payload);
      setForm(data); toast.success("Enquiry form saved — live on the website now");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!confirm("Reset the enquiry form to the default fields?")) return;
    const { data } = await api.post("/admin/enquiry-form/reset"); setForm(data); toast.success("Reset to defaults");
  };

  return (
    <div className="space-y-5" data-testid="enquiry-form-tab">
      <Card className="border-border/70">
        <CardContent className="grid gap-3 p-5 sm:grid-cols-2">
          <div><Label>Popup title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title-input" /></div>
          <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="form-subtitle-input" /></div>
          <div className="sm:col-span-2"><Label>Confirmation message shown after submit</Label><Input value={form.success_message} onChange={(e) => setForm({ ...form, success_message: e.target.value })} data-testid="form-success-input" /></div>
        </CardContent>
      </Card>
      <div className="space-y-3">
        {form.fields.map((f, i) => (
          <FieldRow key={f.key} f={f} i={i} n={form.fields.length} onChange={(p) => setField(i, p)} onMove={move} onDelete={() => remove(i)} />
        ))}
      </div>
      <div className="sticky bottom-4 flex flex-wrap justify-between gap-2 rounded-xl border border-border bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="flex gap-2">
          <Button variant="outline" onClick={add} data-testid="form-add-field"><Plus className="mr-1.5 h-4 w-4" /> Add question</Button>
          <Button variant="ghost" onClick={reset} data-testid="form-reset"><RotateCcw className="mr-1.5 h-4 w-4" /> Reset to defaults</Button>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="form-save"><Save className="mr-1.5 h-4 w-4" /> Save form</Button>
      </div>
    </div>
  );
}
